import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { z } from 'zod'
import {
  CaptchaModal,
  apiClient,
  useAgentContext,
  upsertSavedAccount,
  getSupabase,
  ApiClientError,
} from '@dogdisk/shared'
import { MobileScreen, TopBar } from '../../components/mobile-screen'

const schema = z.object({
  username: z.string().min(3).max(32).regex(/^[a-zA-Z0-9_-]+$/, '用户名只能含字母数字 _ -'),
  password: z.string().min(8).max(128, '密码 8-128 字符'),
  email: z.string().email().optional().or(z.literal('')),
})

type RegisterResp = {
  user: {
    user_id: string
    username: string
    first_agent_id: string
    needs_email_verify: boolean
    needs_phone_verify: boolean
  }
  access_token?: string
  refresh_token?: string
}

function randomString(len: number): string {
  const chars = 'aAbBcCdDeEfFgGhHiIjJkKlLmMnNoOpPqQrRsStTuUvVwWxXyYzZ0123456789'
  let out = ''
  for (let i = 0; i < len; i++) out += chars.charAt(Math.floor(Math.random() * chars.length))
  return out
}

export function RegisterPage() {
  const navigate = useNavigate()
  const { agent_id, referrer_user_id } = useAgentContext()
  const [username, setUsername] = useState(() => randomString(11))
  const [password, setPassword] = useState(() => randomString(11))
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [errMsg, setErrMsg] = useState<string | null>(null)
  const [captchaOpen, setCaptchaOpen] = useState(false)
  const [pending, setPending] = useState<{ username: string; password: string; email: string } | null>(null)
  // 2026-05-28 dg-fix-user2 §4 梯度 captcha
  const [requiredCount, setRequiredCount] = useState(1)

  const submitWithCaptcha = async (
    vals: { username: string; password: string; email: string },
    captchaTokens?: string[],
  ) => {
    setBusy(true)
    setErrMsg(null)
    try {
      const parsed = schema.parse(vals)
      const res = await apiClient.post<RegisterResp>('/gapi/public/auth/register', {
        username: parsed.username,
        email: parsed.email || undefined,
        password: parsed.password,
        captcha_tokens: captchaTokens ?? [],
      })
      // 2026-05-28 dg-fix-user2 §5 注册即登录(setSession);saved 列表存账号+密码(明文)
      // 用于下次"点击 saved → 填表单"。owner 决策推翻 §8.9 不存密码约束。
      if (res.access_token && res.refresh_token) {
        await getSupabase().auth.setSession({
          access_token: res.access_token,
          refresh_token: res.refresh_token,
        })
        upsertSavedAccount({
          user_id: res.user.user_id,
          username: res.user.username,
          avatar_url: null,
          password: parsed.password,
          last_login_at: new Date().toISOString(),
        })
        navigate('/')
      } else {
        // supabase signin 失败兜底:saved 仍存(让用户下次点 saved 直接填表单)+ 跳登录页
        upsertSavedAccount({
          user_id: res.user.user_id,
          username: res.user.username,
          avatar_url: null,
          password: parsed.password,
          last_login_at: new Date().toISOString(),
        })
        navigate(`/auth/login?username=${encodeURIComponent(parsed.username)}`)
      }
    } catch (e) {
      if (e instanceof ApiClientError && e.code === 'CAPTCHA_REQUIRED') {
        const req = (e.details as { required_count?: number } | undefined)?.required_count ?? 1
        setRequiredCount(req)
        setPending(vals)
        setCaptchaOpen(true)
        return
      }
      setErrMsg(e instanceof Error ? e.message : '注册失败')
    } finally {
      setBusy(false)
    }
  }

  return (
    <MobileScreen>
      <TopBar />
      <main data-testid="9APGY" className="flex flex-col gap-6 px-6 pt-2">
        <h1 className="text-[26px] font-bold text-[#1A1A1A]">快速注册</h1>

        {(agent_id || referrer_user_id) && (
          <div className="rounded-lg bg-[#F6F7F8] p-3 text-[12px] text-[#9CA3AF]">
            {agent_id && <p>代理: <span className="font-mono text-[#1A1A1A]">{agent_id}</span></p>}
            {referrer_user_id && <p>推荐人: <span className="font-mono text-[#1A1A1A]">{referrer_user_id}</span></p>}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault()
            void submitWithCaptcha({ username, password, email })
          }}
          className="flex flex-col gap-4"
        >
          <label className="flex flex-col gap-2">
            <span className="text-[14px] font-semibold text-[#1A1A1A]">用户名</span>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              className="h-12 rounded-lg bg-[#F1F2F4] px-4 text-[14px] tracking-wide outline-none focus:bg-[#EBEDEF]"
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-[14px] font-semibold text-[#1A1A1A]">密码</span>
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              className="h-12 rounded-lg bg-[#F1F2F4] px-4 text-[14px] tracking-wide outline-none focus:bg-[#EBEDEF]"
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-[14px] font-semibold text-[#1A1A1A]">邮箱地址(选填)</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="请输入邮箱地址"
              autoComplete="email"
              className="h-12 rounded-lg bg-[#F1F2F4] px-4 text-[14px] outline-none placeholder:text-[#B4B7BC] focus:bg-[#EBEDEF]"
            />
          </label>

          {errMsg && <p className="text-[13px] text-[#FF6B6B]">{errMsg}</p>}

          <button
            type="submit"
            disabled={busy}
            className="mt-2 h-12 rounded-lg bg-[#19caad] text-[15px] font-semibold text-white active:bg-[#14a18b] disabled:opacity-50"
          >
            {busy ? '注册中…' : '快速注册'}
          </button>

          <p className="self-center text-[13px] text-[#9CA3AF]">
            已有账号？<Link to="/auth/login" className="font-semibold text-[#19caad]">立即登录</Link>
          </p>
        </form>
      </main>

      <CaptchaModal
        open={captchaOpen}
        onClose={() => setCaptchaOpen(false)}
        requiredCount={requiredCount}
        onVerified={(tokens) => {
          // 验证成功后 modal 不再自调 onClose,由本处关闭(2026-06-06 captcha fix)
          setCaptchaOpen(false)
          if (pending) void submitWithCaptcha(pending, tokens)
        }}
      />
    </MobileScreen>
  )
}
